import { z } from "zod";
import { CUSTOM_PRINT_DESIGN_VALUES, EVENT_TYPE_VALUES, GUEST_COUNT_VALUES } from "./eventTypes";
import { DATE_FORMAT_REGEX, TIME_FORMAT_REGEX, eventDurationMinutes } from "../utils/time";

// Gedeeld tussen client (formuliervalidatie, foutmeldingen naast het veld) en
// server (autoritatieve herkeuring). Vertrouw nooit alleen op de clientkant.

const PHONE_REGEX = /^[0-9+()\-.\s]{6,40}$/;

function normalizeEmail(value: string): string {
  const trimmed = value.trim();
  const atIndex = trimmed.lastIndexOf("@");
  if (atIndex === -1) return trimmed;
  const localPart = trimmed.slice(0, atIndex);
  const domainPart = trimmed.slice(atIndex + 1).toLowerCase();
  return `${localPart}@${domainPart}`;
}

const emailField = z
  .string()
  .trim()
  .min(3, "Vul een geldig e-mailadres in.")
  .max(254, "E-mailadres is te lang.")
  .transform(normalizeEmail)
  .pipe(z.string().email("Vul een geldig e-mailadres in."));

const phoneField = z.preprocess(
  (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
  z
    .string()
    .trim()
    .regex(PHONE_REGEX, "Vul een geldig telefoonnummer in, of laat het veld leeg.")
    .optional(),
);

export const eventDateField = z.string().regex(DATE_FORMAT_REGEX, "Kies een geldige datum.");

export const timeField = z.string().regex(TIME_FORMAT_REGEX, "Kies een geldige tijd.");

/**
 * Contact- en evenementgegevens uit stap 2 van het formulier. Bevat geen
 * beveiligingsvelden (Turnstile, honeypot, timing) - die worden apart
 * gevalideerd rond het transportlaagje in de submission-schema hieronder.
 */
export const bookingDetailsSchema = z
  .object({
    eventDate: eventDateField,
    startTime: timeField,
    endTime: timeField,
    endNextDay: z.boolean(),

    fullName: z
      .string()
      .trim()
      .min(1, "Vul je voor- en achternaam in.")
      .max(200, "Naam is te lang."),
    email: emailField,
    phone: phoneField,

    location: z
      .string()
      .trim()
      .min(1, "Vul de locatie van het evenement in.")
      .max(300, "Locatie is te lang."),

    eventType: z.enum(EVENT_TYPE_VALUES, {
      errorMap: () => ({ message: "Kies het type evenement." }),
    }),
    eventTypeOther: z.preprocess(
      (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
      z.string().trim().max(200, "Omschrijving is te lang.").optional(),
    ),

    guestCount: z.preprocess(
      (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
      z.enum(GUEST_COUNT_VALUES).optional(),
    ),

    customPrintDesign: z.enum(CUSTOM_PRINT_DESIGN_VALUES, {
      errorMap: () => ({ message: "Kies een optie voor het printontwerp." }),
    }),

    comments: z.preprocess(
      (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
      z.string().trim().max(2000, "Opmerkingen zijn te lang (max. 2.000 tekens).").optional(),
    ),
  })
  .superRefine((data, ctx) => {
    if (data.eventType === "other") {
      if (!data.eventTypeOther || data.eventTypeOther.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Vertel ons wat voor evenement het is.",
          path: ["eventTypeOther"],
        });
      }
    }

    const duration = eventDurationMinutes(data.startTime, data.endTime, data.endNextDay);
    if (duration <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: data.endNextDay
          ? "Controleer de begin- en eindtijd: de eindtijd valt niet na de begintijd."
          : "De eindtijd moet na de begintijd liggen. Vink 'eindtijd is na middernacht' aan als dat van toepassing is.",
        path: ["endTime"],
      });
    } else if (duration > 20 * 60) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Deze tijdsduur lijkt niet te kloppen. Controleer de begin- en eindtijd.",
        path: ["endTime"],
      });
    }
  });

export type BookingDetailsInput = z.input<typeof bookingDetailsSchema>;
export type BookingDetails = z.output<typeof bookingDetailsSchema>;

/**
 * Volledige payload die de client naar POST /api/requests stuurt: de
 * formuliergegevens plus de beveiligingsvelden. De server valideert dit
 * geheel opnieuw en vertrouwt niets uit de client blind.
 */
export const bookingSubmissionSchema = bookingDetailsSchema.and(
  z.object({
    turnstileToken: z.string().min(1, "Beveiligingscontrole ontbreekt."),
    honeypot: z.string().max(0).optional().default(""),
    formRenderedAtMs: z.number().int().positive("Ongeldige verzendtijd."),
    idempotencyKey: z.string().uuid("Ongeldige aanvraag-identifier."),
  }),
);

export type BookingSubmissionInput = z.input<typeof bookingSubmissionSchema>;
export type BookingSubmission = z.output<typeof bookingSubmissionSchema>;

export const availabilityQuerySchema = z.object({
  from: eventDateField,
  to: eventDateField,
});
